/**
 * CentersPage - Gestione Centri Organization
 * Il Diario di Bordo - SaaS
 * 
 * FASE 5: Pagina elenco centri per il Responsabile
 * 
 * FIX: Corretta estrazione dati API (res.data.centers invece di res.data.data?.centers)
 */

import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useNotification } from '../../contexts/NotificationContext'
import { organizationApi } from '../../services/organizationApi'
import { PageLoader, Card, EmptyState } from '../../components/ui'

import {
  Building2,
  Plus,
  Search,
  MoreVertical,
  Eye,
  Edit,
  Pause,
  Play,
  Trash2,
  Loader2,
  Users,
  Baby,
  AlertTriangle
} from 'lucide-react'

// ============================================
// STATUS BADGE
// ============================================

function StatusBadge({ status }) {
  const config = {
    trial: { label: 'Trial', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    active: { label: 'Attivo', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
    suspended: { label: 'Sospeso', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
    pending_deletion: { label: 'In Cancellazione', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    export_only: { label: 'Solo Esportazione', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
    cancelled: { label: 'Cancellato (legacy)', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' },
    deleted: { label: 'Eliminato', color: 'bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-500' }
  }

  const { label, color } = config[status] || {
    label: 'Sconosciuto',
    color: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
  }
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${color}`}>
      {label}
    </span>
  )
}

// ============================================
// CENTER CARD (per layout cards)
// ============================================

function CenterCard({ center, onAction }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()
  
  const hasCoordinator = center.coordinatoreId || center.stats?.coordinatori > 0 || center.usage?.coordinatori > 0
  
  return (
    <div 
      className={`bg-white dark:bg-gray-800 rounded-xl border-2 ${
        !hasCoordinator 
          ? 'border-yellow-300 dark:border-yellow-600' 
          : 'border-gray-200 dark:border-gray-700'
      } p-5 hover:shadow-lg transition-all cursor-pointer`}
      onClick={() => navigate(`/organization/centers/${center._id}`)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Building2 className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {center.name}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {center.entityType || 'privato'}
            </p>
          </div>
        </div>
        <StatusBadge status={center.status} />
      </div>
      
      {/* Alert no coordinator */}
      {!hasCoordinator && (
        <div className="flex items-center gap-2 mb-4 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-yellow-700 dark:text-yellow-400 text-sm">
          <AlertTriangle className="h-4 w-4" />
          <span>Nessun coordinatore</span>
        </div>
      )}
      
      {/* Stats */}
      <div className="flex items-center justify-around py-3 border-t border-b border-gray-100 dark:border-gray-700">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {center.stats?.minori || center.usage?.minori || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Minori</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {center.stats?.educatori || center.usage?.educatori || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Educatori</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {center.stats?.coordinatori || center.usage?.coordinatori || 0}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Coordinatori</p>
        </div>
      </div>
      
      {/* Trial info */}
      {center.status === 'trial' && center.trialEndsAt && (
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span className="text-lg">⏱</span>
          {(() => {
            const daysLeft = Math.ceil((new Date(center.trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24))
            return daysLeft > 0 
              ? `${daysLeft} giorni rimanenti nel trial`
              : 'Trial scaduto'
          })()}
        </div>
      )}
      
      {/* Menu azioni */}
      <div className="relative mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen(!menuOpen)
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <MoreVertical className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        {menuOpen && (
          <>
            <div 
              className="fixed inset-0 z-10" 
              onClick={(e) => {
                e.stopPropagation()
                setMenuOpen(false)
              }}
            />
            <div className="absolute right-0 bottom-12 z-20 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onAction('view', center)
                  setMenuOpen(false)
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <Eye className="h-4 w-4" />
                Visualizza
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onAction('edit', center)
                  setMenuOpen(false)
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Modifica
              </button>
              
              {center.status === 'active' || center.status === 'trial' ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onAction('suspend', center)
                    setMenuOpen(false)
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 flex items-center gap-2"
                >
                  <Pause className="h-4 w-4" />
                  Sospendi
                </button>
              ) : center.status === 'suspended' ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onAction('reactivate', center)
                    setMenuOpen(false)
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center gap-2"
                >
                  <Play className="h-4 w-4" />
                  Riattiva
                </button>
              ) : null}
              
              {center.status !== 'pending_deletion' && center.status !== 'deleted' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onAction('delete', center)
                    setMenuOpen(false)
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Richiedi Cancellazione
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ============================================
// CENTERS LIST PAGE
// ============================================

export default function CentersPage() {
  const [loading, setLoading] = useState(true)
  const [centers, setCenters] = useState([])
  const [filteredCenters, setFilteredCenters] = useState([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [actionModal, setActionModal] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionReason, setActionReason] = useState('')
  
  const navigate = useNavigate()
  const { showSuccess, showError } = useNotification()
  
  const loadCenters = useCallback(async () => {
    try {
      setLoading(true)
      const res = await organizationApi.getCenters()
      
      // FIX: L'API restituisce { success: true, centers: [...] }
      // NON { data: { centers: [...] } }
      const centersData = res.data.centers || res.data.data?.centers || []
      setCenters(centersData)
    } catch (err) {
      console.error('Error loading centers:', err)
      showError('Errore nel caricamento dei centri')
    } finally {
      setLoading(false)
    }
  }, [showError])
  
  useEffect(() => {
    loadCenters()
  }, [loadCenters])
  
  // Filter centers
  useEffect(() => {
    let filtered = [...centers]
    
    if (search) {
      const searchLower = search.toLowerCase()
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(searchLower) ||
        c.slug?.toLowerCase().includes(searchLower)
      )
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter)
    }
    
    setFilteredCenters(filtered)
  }, [centers, search, statusFilter])
  
  // Handle actions
  const handleAction = (action, center) => {
    switch (action) {
      case 'view':
        navigate(`/organization/centers/${center._id}`)
        break
      case 'edit':
        navigate(`/organization/centers/${center._id}/edit`)
        break
      case 'suspend':
      case 'reactivate':
      case 'delete':
        setActionModal({ action, center })
        setActionReason('')
        break
      default:
        break
    }
  }
  
  const executeAction = async () => {
    if (!actionModal) return
    
    const { action, center } = actionModal
    setActionLoading(true)
    
    try {
      switch (action) {
        case 'suspend':
          await organizationApi.suspendCenter(center._id, actionReason)
          showSuccess('Centro sospeso')
          break
        case 'reactivate':
          await organizationApi.reactivateCenter(center._id)
          showSuccess('Centro riattivato')
          break
        case 'delete':
          await organizationApi.requestCenterDeletion(center._id, actionReason)
          showSuccess('Richiesta cancellazione inviata')
          break
      }
      
      setActionModal(null)
      loadCenters()
    } catch (err) {
      showError(err.response?.data?.message || 'Errore nell\'operazione')
    } finally {
      setActionLoading(false)
    }
  }
  
  if (loading) {
    return <PageLoader />
  }
  
  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Gestione Centri
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {centers.length} {centers.length === 1 ? 'centro registrato' : 'centri registrati'}
          </p>
        </div>
        
        <Link to="/organization/centers/new" className="btn-primary">
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Centro
        </Link>
      </div>
      
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca centro..."
              className="input pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-full md:w-48"
          >
            <option value="all">Tutti gli stati</option>
            <option value="trial">Trial</option>
            <option value="active">Attivi</option>
            <option value="suspended">Sospesi</option>
            <option value="pending_deletion">In Cancellazione</option>
          </select>
        </div>
      </div>
      
      {/* Centers Grid */}
      {filteredCenters.length === 0 ? (
        <EmptyState
          icon={Building2}
          title={search || statusFilter !== 'all' ? 'Nessun centro trovato' : 'Nessun centro registrato'}
          description={search || statusFilter !== 'all' 
            ? 'Prova a modificare i filtri di ricerca'
            : 'Crea il tuo primo centro per iniziare'
          }
          action={
            !search && statusFilter === 'all' && (
              <Link to="/organization/centers/new" className="btn-primary">
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Centro
              </Link>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCenters.map(center => (
            <CenterCard 
              key={center._id} 
              center={center} 
              onAction={handleAction}
            />
          ))}
        </div>
      )}
      
      {/* Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {actionModal.action === 'suspend' && 'Sospendi Centro'}
              {actionModal.action === 'reactivate' && 'Riattiva Centro'}
              {actionModal.action === 'delete' && 'Richiedi Cancellazione'}
            </h3>
            
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {actionModal.action === 'suspend' && `Stai per sospendere "${actionModal.center.name}". Gli utenti non potranno più accedere.`}
              {actionModal.action === 'reactivate' && `Stai per riattivare "${actionModal.center.name}". Gli utenti potranno nuovamente accedere.`}
              {actionModal.action === 'delete' && `Stai per richiedere la cancellazione di "${actionModal.center.name}". Questa azione non può essere annullata facilmente.`}
            </p>
            
            {(actionModal.action === 'suspend' || actionModal.action === 'delete') && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Motivo {actionModal.action === 'delete' ? '(obbligatorio)' : '(opzionale)'}
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="Inserisci il motivo..."
                  required={actionModal.action === 'delete'}
                />
              </div>
            )}
            
            {actionModal.action === 'delete' && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-medium">Attenzione</span>
                </div>
                <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                  Tutti i dati del centro verranno eliminati permanentemente.
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setActionModal(null)}
                disabled={actionLoading}
                className="btn-secondary"
              >
                Annulla
              </button>
              <button
                onClick={executeAction}
                disabled={actionLoading || (actionModal.action === 'delete' && !actionReason.trim())}
                className={
                  actionModal.action === 'delete'
                    ? 'btn-danger'
                    : actionModal.action === 'suspend'
                      ? 'bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50'
                      : 'btn-primary'
                }
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {actionModal.action === 'suspend' && 'Sospendi'}
                    {actionModal.action === 'reactivate' && 'Riattiva'}
                    {actionModal.action === 'delete' && 'Conferma Cancellazione'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


